const STORAGE_KEY = "affiliate_notice_last_shown";
const SHOW_INTERVAL = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

export const noticeStorage = {
  // Check if the notice should be shown
  shouldShowNotice: () => {
    try {
      const lastShown = localStorage.getItem(STORAGE_KEY);
      const now = Date.now();

      if (!lastShown) {
        // First time - show modal
        return true;
      }

      const lastShownTime = parseInt(lastShown, 10);
      const timeSinceLastShown = now - lastShownTime;

      // Show if more than 6 hours have passed
      return timeSinceLastShown >= SHOW_INTERVAL;
    } catch (error) {
      console.warn("Error checking notice storage:", error);
      return true; // Show on error to be safe
    }
  },

  // Mark the notice as shown
  markAsShown: () => {
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch (error) {
      console.warn("Error saving notice timestamp:", error);
    }
  },

  // Get the last shown timestamp
  getLastShownTime: () => {
    try {
      const lastShown = localStorage.getItem(STORAGE_KEY);
      return lastShown ? new Date(parseInt(lastShown, 10)) : null;
    } catch (error) {
      console.warn("Error getting last shown time:", error);
      return null;
    }
  },

  // Get time until next show
  getTimeUntilNextShow: () => {
    try {
      const lastShown = localStorage.getItem(STORAGE_KEY);
      if (!lastShown) return 0;

      const lastShownTime = parseInt(lastShown, 10);
      const now = Date.now();
      const timeSinceLastShown = now - lastShownTime;
      const timeUntilNext = SHOW_INTERVAL - timeSinceLastShown;

      return Math.max(0, timeUntilNext);
    } catch (error) {
      console.warn("Error calculating time until next show:", error);
      return 0;
    }
  },

  // Reset the notice (for testing purposes)
  reset: () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log("🔄 Notice storage reset - modal will show on next visit");
    } catch (error) {
      console.warn("Error resetting notice storage:", error);
    }
  },

  // Debug function to show current status
  getDebugInfo: () => {
    try {
      const lastShown = localStorage.getItem(STORAGE_KEY);
      const shouldShow = noticeStorage.shouldShowNotice();
      const timeUntilNext = noticeStorage.getTimeUntilNextShow();
      const hoursUntilNext = Math.ceil(timeUntilNext / (60 * 60 * 1000));

      return {
        lastShown: lastShown
          ? new Date(parseInt(lastShown, 10)).toLocaleString()
          : "Never",
        shouldShow,
        timeUntilNext: timeUntilNext > 0 ? `${hoursUntilNext} hours` : "Now",
        storageKey: STORAGE_KEY,
        intervalHours: SHOW_INTERVAL / (60 * 60 * 1000),
      };
    } catch (error) {
      console.warn("Error getting debug info:", error);
      return { error: error.message };
    }
  },
};

// Make debug functions available globally for testing
if (typeof window !== "undefined") {
  window.noticeDebug = {
    reset: noticeStorage.reset,
    getDebugInfo: noticeStorage.getDebugInfo,
    shouldShow: noticeStorage.shouldShowNotice,
  };
}

export default noticeStorage;
